<template>
  <v-dialog v-model="show" max-width="500" persistent scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            icon
            color="primary"
            v-bind="attrs"
            v-on="{ ...on, ...tooltip }"
            :disabled="!connectionId"
          >
            <v-icon>mdi-map-search-outline</v-icon>
          </v-btn>
        </template>
        <span v-if="!connectionId">Select a connection first</span>
        <span v-else>Upload shapefile to find wells within boundaries</span>
      </v-tooltip>
    </template>
    <v-card :loading="loading" :disabled="loading">
      <template slot="progress">
        <v-progress-linear indeterminate></v-progress-linear>
      </template>
      <v-card-title>Upload Shapefile</v-card-title>
      <v-card-subtitle>Upload a ZIP file containing a shapefile to find wells within boundaries</v-card-subtitle>
      <v-card-text>
        <v-file-input
          v-model="file"
          label="Shapefile ZIP"
          accept=".zip"
          :rules="fileRules"
          show-size
          clearable
          hint="Select a ZIP file containing shapefile data (.shp, .dbf, .prj, .shx)"
          persistent-hint
        ></v-file-input>
        
        <v-alert v-if="error" type="error" dense text class="mt-4">
          {{ error }}
        </v-alert>
        
        <v-alert v-if="result" type="success" dense text class="mt-4">
          <div><strong>{{ result.shapefile_name }}</strong></div>
          <div>Found {{ result.wells_found }} wells</div>
          <div v-if="result.bounds">
            Bounds: {{ result.bounds[0].toFixed(3) }}, {{ result.bounds[1].toFixed(3) }} to 
            {{ result.bounds[2].toFixed(3) }}, {{ result.bounds[3].toFixed(3) }}
          </div>
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn text color="success" @click="upload" :disabled="!file || loading">Upload</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Connections from '@/api/connections'

export default {
  name: 'ShapeFileDialog',
  props: {
    connectionId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      show: false,
      loading: false,
      file: null,
      result: null,
      error: null,
      fileRules: [
        v => !!v || 'File is required',
        v => !v || v.size < 50000000 || 'File size should be less than 50 MB',
        v => !v || v.name.endsWith('.zip') || 'File must be a ZIP archive'
      ]
    }
  },
  methods: {
    async upload() {
      if (!this.file) {
        return
      }
      
      this.loading = true
      this.error = null
      this.result = null
      
      try {
        // Upload the file to the lookup endpoint
        const response = await Connections.lookup(this.connectionId, this.file)
        
        this.result = response.data
        
        // Emit the APIs to the parent component
        if (response.data.apis && response.data.apis.length > 0) {
          this.$emit('wells-found', response.data.apis)
          // this.notify(`Found ${response.data.apis.length} wells in shapefile`, 'success')
          
          // Close dialog immediately
          this.show = false
        } else {
          this.error = 'No wells found within shapefile boundaries'
        }
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.detail 
          ? error.response.data.detail 
          : 'Failed to process shapefile'
        this.notify(this.error, 'error')
      } finally {
        this.loading = false
      }
    },
    cancel() {
      this.show = false
      this.file = null
      this.result = null
      this.error = null
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.file = null
        this.result = null
        this.error = null
      }
    }
  }
}
</script>
